"use client";

import { ApolloClient, InMemoryCache, createHttpLink } from "@apollo/client";
import { setContext } from "@apollo/client/link/context";
import { getYzsaId } from "@yardzen/next-api-util/yzsa-utils";
import {
  AccessTokenResult,
  InvalidCredentialsException,
  getAccessTokenResult,
  isInvalidCredentialsException,
} from "./getAccessToken";
import { inBrowser } from "./inBrowser";

/**
 * Raised instead of sending an authenticated request with no token.
 *
 * Surfaces to the caller as an Apollo network error, so a hook can tell
 * "could not ask" apart from a genuine authorization failure and retry rather
 * than treat it as a denial.
 */
export class AccessTokenUnavailableError extends Error {
  constructor() {
    super("No access token available for an authenticated request");
    this.name = "AccessTokenUnavailableError";
  }
}

export function GQLClient(
  handleInvalidCredentials = defaultInvalidCredentialsHandler,
) {
  const httpLink = createHttpLink({
    uri: "/api/graphql",
    fetchOptions: {
      credentials: "omit" as RequestCredentials,
    },
  });

  const authLink = setContext(async (_, context) => {
    const { headers, noauth } = context as {
      headers?: Record<string, string>;
      noauth?: boolean;
    };

    let result: AccessTokenResult = { status: "anonymous" };

    try {
      result = await getAccessTokenResult();
    } catch (error) {
      if (isInvalidCredentialsException(error)) {
        handleInvalidCredentials(error);
      }
    }

    // The visitor is signed in but no token is available, so the API would
    // reject this request as Unauthorized. Fail here instead of sending it:
    // a blank Authorization header turns a recoverable client-side race into
    // a server-side auth failure, which callers cannot tell apart from a real
    // one. (This was ~28% of terms-of-service checks.)
    //
    // Opt out with `context: { noauth: true }` for a query that is meant to
    // work signed out.
    if (result.status === "unavailable" && !noauth) {
      throw new AccessTokenUnavailableError();
    }

    const token = result.status === "token" ? result.token : undefined;

    // Get localStorage yzsa value to send in header for middleware
    const localStorageYzsa = getYzsaId();

    return {
      headers: {
        ...headers,
        authorization: token ? `Bearer ${token}` : "",
        // Send localStorage yzsa value to middleware if available
        ...(localStorageYzsa && { "x-yzsa-localstorage": localStorageYzsa }),
      },
    };
  });

  return new ApolloClient({
    link: authLink.concat(httpLink),
    cache: new InMemoryCache(),
    devtools: {
      enabled: shouldConnectToApolloDevTools(),
    },
  });
}

function shouldConnectToApolloDevTools() {
  if (!inBrowser() || typeof window.location === "undefined") {
    return false;
  }
  return (
    window.location.hostname === "localhost" ||
    window.location.hostname === "127.0.0.1"
  );
}

/**
 * Default handler for invalid credentials. Do not use on eden
 */
export function defaultInvalidCredentialsHandler(
  error: InvalidCredentialsException,
): void {
  // Already on the login page, which is where this would send the visitor.
  // Redirecting again reloads /login, the stale session fires this handler
  // again, and the visitor is stuck in a reload loop they can't type through
  // (EN-6668). Signing in replaces the stale session.
  if (window.location.pathname === "/login") {
    return;
  }

  const url = new URL("/login", window.location.origin);
  url.searchParams.set("error", error.error);
  url.searchParams.set(
    "callbackUrl",
    window.location.pathname + window.location.search,
  );
  url.searchParams.set("logout", "true");
  window.location.replace(url.toString());
}
